import { DayGridViewState } from 'owa-day-grid-core-schema';
import { SurfaceConstants } from 'owa-calendar-surface-core';
import { isSameDay, OwaDate, startOfDay, getTimestamp } from 'owa-datetime';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import * as React from 'react';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { useCustomAnimationFrame } from 'owa-react-hooks/lib/useCustomAnimationFrame';

const { scrollTopAdjustment } = SurfaceConstants;

export interface DayGridScrollHooksProps {
    viewState: DayGridViewState;
}

export default function useScrollToSelectionItemHooks(props: DayGridScrollHooksProps) {
    let parentElementRef = null as React.MutableRefObject<HTMLElement>;

    const [requestScrollFrame, , isScrollRequestActive] = useCustomAnimationFrame();

    React.useEffect(() => {
        scrollToTime(
            props.viewState.selectionItem,
            props.viewState.selectedDate,
            parentElementRef?.current,
            requestScrollFrame,
            isScrollRequestActive,
            true
        );
    }, [
        parentElementRef,
        props.viewState.selectionItem,
        props.viewState.selectedDate,
        requestScrollFrame,
        isScrollRequestActive,
    ]);

    return React.useCallback(
        (ref: React.MutableRefObject<HTMLElement>) => {
            if (parentElementRef !== ref) {
                parentElementRef = ref;
                scrollToTime(
                    props.viewState.selectionItem,
                    props.viewState.selectedDate,
                    parentElementRef?.current,
                    requestScrollFrame,
                    isScrollRequestActive,
                    false
                );
            }

            return {
                children: null,
                handlers: {},
            };
        },
        [
            props.viewState.selectionItem,
            props.viewState.selectedDate,
            requestScrollFrame,
            isScrollRequestActive,
        ]
    );
}

function scrollToTime(
    selectionItem: CalendarEvent,
    selectedDate: OwaDate,
    scrollContainer: HTMLElement | undefined,
    requestScrollFrame: (callback: () => void) => void,
    isScrollRequestActive: () => boolean,
    shouldScrollForAllDayEvent: boolean
) {
    if (!selectionItem) {
        return;
    }
    const { Start, End, IsAllDayEvent } = selectionItem;

    if (selectedDate === null || (!shouldScrollForAllDayEvent && IsAllDayEvent)) {
        // If there is nothing selected or the event is all day, do not scroll at all
        return;
    }

    if (scrollContainer && !isScrollRequestActive()) {
        const { WorkHoursStartTimeInMinutes } = getUserConfiguration().UserOptions.WorkingHours;
        if (!isSameDay(Start, selectedDate) && !isSameDay(End, selectedDate)) {
            scrollContainer.scrollTop = WorkHoursStartTimeInMinutes - scrollTopAdjustment;
            return;
        }

        const baseTime = startOfDay(selectedDate);
        const startOffset =
            IsAllDayEvent && shouldScrollForAllDayEvent
                ? WorkHoursStartTimeInMinutes
                : getOffset(Start, baseTime);
        const endOffset = getOffset(End, baseTime);

        // execute the GBCR after the current render cycle finishes
        requestScrollFrame(() => {
            // perform scroll if start end is off the boundaries
            if (
                isSameDay(selectedDate, Start) &&
                (startOffset < scrollContainer.scrollTop ||
                    startOffset >
                        scrollContainer.scrollTop +
                            scrollContainer.clientHeight -
                            scrollTopAdjustment)
            ) {
                scrollContainer.scrollTop = startOffset - scrollTopAdjustment;
            } else if (
                isSameDay(selectedDate, End) &&
                (endOffset < scrollContainer.scrollTop ||
                    endOffset >
                        scrollContainer.scrollTop +
                            scrollContainer.clientHeight -
                            scrollTopAdjustment)
            ) {
                scrollContainer.scrollTop = endOffset - scrollTopAdjustment;
            }
        });
    }
}

function getOffset(time: OwaDate, baseTime: OwaDate) {
    return Math.ceil(
        (getTimestamp(time) - getTimestamp(baseTime)) / TimeConstants.MillisecondsIn1Min
    );
}
